import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TogglePill, Tag } from "@/components/ds";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSectionHeader } from '@/hooks/useSectionHeaders';
import { RenderHeaderParts } from '@/components/ds/RenderCMSHeader';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';
import { useContentHover } from '@/hooks/useContentHover';
import { optimizeImage } from '@/lib/imageOptimization';
import { useSmartImage } from '@/hooks/useSmartImage';

/**
 * Vision Section (About page)
 * 
 * CMS Integration:
 * - Uses useSectionHeader('about', 'vision') for header/tag/subheader
 * 
 * Simple manual toggle between Institutional and Direct paths.
 */

type PathType = "institutional" | "direct";

const pathData = {
  institutional: {
    label: "The Institutional Path",
    title: "Built for qualified investors.",
    description: "A structured, hands-off approach to private markets – for those who meet the criteria.",
    pills: ["High minimums", "Fixed terms", "Fixed rates", "Advice required"],
    image: "https://images.pexels.com/photos/3093034/pexels-photo-3093034.jpeg",
    imagePosition: "center center",
  },
  direct: {
    label: "The Direct Path",
    title: "Built for hands-on investors.",
    description: "Choose your own exposure, track your assets, and invest on your terms.",
    pills: ["Low minimums", "Flexible terms", "Variable returns", "Direct access"],
    image: "https://images.pexels.com/photos/8555499/pexels-photo-8555499.jpeg",
    imagePosition: "center 20%",
  },
};

const toggleOptions = [
  { value: "institutional", label: "Institutional Path" },
  { value: "direct", label: "Direct Path" },
];

// ⚠️ ANALYTICS: Card hover wrapper for vision path content
function VisionPathContent({ activePath, children, ...props }: {
  activePath: PathType;
  children: React.ReactNode;
  [key: string]: any;
}) {
  const { contentHoverProps } = useContentHover({
    contentId: `vision-${activePath}`,
    contentType: 'card',
    contentTitle: pathData[activePath].label,
    contentSection: 'vision',
    contentPage: 'about',
    contentPosition: activePath === 'institutional' ? 0 : 1,
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      className="relative rounded-3xl overflow-hidden min-h-[400px] lg:min-h-[500px]"
      {...contentHoverProps}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export default function VisionSection() {
  const [activePath, setActivePath] = useState<PathType>("institutional");
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);
  const [imageLoaded, setImageLoaded] = useState(false);
  const currentPath = pathData[activePath];
  const isDirect = activePath === "direct";
  const isMobile = useIsMobile();
  
  // CMS data
  const sectionHeader = useSectionHeader('about', 'vision');

  // Part B: Increased rootMargin — fires observer 2000px before viewport entry
  const { ref: smartImageRef, inView: imageInView } = useSmartImage('0px 0px 2000px 0px');

  // ⚠️ ANALYTICS: Section visibility tracking
  const { ref: sectionRef } = useSectionVisibility({
    sectionId: 'vision',
    sectionName: 'Our Vision',
    pageName: 'About',
    position: 1,
  });

  // ⚠️ ANALYTICS: Track time on each path
  const pathViewStart = useRef<number>(Date.now());

  // Part A: Mount-time preload — preload BOTH vision path images immediately on About page load
  useEffect(() => {
    const links: HTMLLinkElement[] = [];
    Object.values(pathData).forEach((path) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = optimizeImage(path.image, { width: 1400 });
      document.head.appendChild(link);
      links.push(link);
    });
    return () => {
      links.forEach((link) => {
        if (document.head.contains(link)) document.head.removeChild(link);
      });
    };
  }, []);

  // Part C: Reset imageLoaded on path toggle for fade-in transition
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  // ⚠️ ANALYTICS: Handle path toggle with tracking
  const handlePathToggle = (value: string) => {
    const newPath = value as PathType;
    const previousPath = activePath;
    const timeOnPreviousPath = Date.now() - pathViewStart.current;

    trackEvent(ANALYTICS_EVENTS.VISION_PATH_TOGGLED, {
      from_path: previousPath,
      to_path: newPath,
      time_on_previous_path_ms: timeOnPreviousPath,
      section: 'vision',
      page: 'About',
    });

    pathViewStart.current = Date.now();
    setImageLoaded(false); // Reset for fade-in on new path
    setActivePath(newPath);
  };

  // Mobile styling: institutional = dark (carbon bg, platinum text), direct = light (grey bg, carbon text)
  const isMobileInstitutional = isMobile && !isDirect;
  const mobileBackgroundColor = isMobileInstitutional 
    ? 'hsl(240 3% 11%)' // carbon
    : 'hsl(240 6% 90%)'; // grey

  return (
    <section 
      id="vision" 
      ref={sectionRef}
      className="relative bg-background py-16 md:py-24"
    >
      {/* molecule-section-header-centered-with-tag */}
      <div className="mb-8">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            {...(hasMounted && { initial: { opacity: 0, y: 30 } })}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col items-center text-center"
          >
            {/* atom-tag-subtle */}
            {sectionHeader?.tag_text && (
              <div className="mb-6">
                <Tag variant="subtle">{sectionHeader.tag_text}</Tag>
              </div>
            )}
            {/* atom-text-section-header + atom-text-section-header-emphasis */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-4">
              <RenderHeaderParts text={sectionHeader?.header_text || 'Same Assets. **Direct Access.**'} />
            </h2>
            {/* atom-text-section-subheader */}
            {sectionHeader?.sub_header && (
              <p className="text-17 text-muted-foreground max-w-2xl">
                {sectionHeader.sub_header}
              </p>
            )}
          </motion.div>
        </div>
      </div>

      {/* molecule-toggle-pill - centered */}
      <div className="mb-8">
        <div className="container mx-auto px-4 md:px-8 flex justify-center">
          <TogglePill
            options={toggleOptions}
            value={activePath}
            onChange={handlePathToggle}
          />
        </div>
      </div>

      {/* Vision Card */}
      <div ref={smartImageRef} className="container mx-auto px-4 md:px-8">
        <AnimatePresence mode="wait">
          <VisionPathContent
            key={activePath}
            activePath={activePath}
            style={{ 
              backgroundColor: isMobile 
                ? mobileBackgroundColor 
                : (isDirect ? '#ffffff' : '#e8e8ed') 
            }}
          >
            {/* Background Image - hidden on mobile for cleaner text readability */}
            {!isMobile && imageInView && (
              <img
                src={optimizeImage(currentPath.image, { width: 1400 })}
                alt="Fedgroup vision"
                loading="lazy"
                decoding="async"
                width={1400}
                height={788}
                onLoad={handleImageLoad}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ 
                  objectPosition: currentPath.imagePosition,
                  transform: 'scaleX(-1)',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 500ms ease-in-out',
                }}
              />
            )}

            {/* Content overlay */}
            <div className="relative z-10 grid lg:grid-cols-2 min-h-[400px] lg:min-h-[500px]">
              <div className={`p-8 md:p-12 lg:p-16 flex flex-col justify-center ${isDirect ? 'lg:col-start-2 lg:items-end lg:text-right' : ''}`}>
                {/* atom-text-card-title */}
                <h3 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 ${isMobileInstitutional ? 'text-platinum' : 'text-foreground'}`}>
                  {currentPath.title}
                </h3>
                
                {/* atom-text-card-body */}
                <p className={`text-lg leading-relaxed mb-8 max-w-md ${isMobileInstitutional ? 'text-platinum' : 'text-foreground'}`}>
                  {currentPath.description}
                </p>
                
                {/* molecule-tag-group */}
                <div className={`flex flex-wrap gap-2 ${isDirect ? 'lg:justify-end' : ''}`}>
                  {currentPath.pills.map((item) => (
                    <span 
                      key={item}
                      className={`px-3 py-1.5 rounded-full text-sm ${
                        isMobileInstitutional 
                          ? 'bg-platinum text-carbon' 
                          : 'bg-foreground text-background'
                      }`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </VisionPathContent>
        </AnimatePresence>
      </div>

      {/* SEO: Hidden content block — exposes the inactive vision path to crawlers */}
      {Object.entries(pathData).map(([key, data]) => {
        if (key === activePath) return null;
        return (
          <div key={`seo-${key}`} className="sr-only">
            <h3>{data.title}</h3>
            <p>{data.description}</p>
            <ul>{data.pills.map((pill) => <li key={pill}>{pill}</li>)}</ul>
          </div>
        );
      })}
    </section>
  );
}
